<template>
  <div class="clinic-revenue-overview">
    <div class="overview-card">
      <!-- 标题栏 -->
      <div class="card-header">
        <div class="header-title">
          <i class="el-icon-s-data"></i>
          <span>诊所营收报表总览</span>
        </div>
      </div>

      <!-- 查询条件 -->
      <div class="query-form">
        <el-form :model="queryParams" size="small" label-width="80px" inline>
          <el-form-item label="统计时间">
            <el-date-picker
                v-model="timeRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd HH:mm:ss"
                :default-time="['00:00:00', '23:59:59']"
                @change="handleTimeChange"
                :picker-options="pickerOptions"
            />
          </el-form-item>
          <el-form-item>
            <el-button @click="handleReset" icon="el-icon-refresh-right">重置</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 统计卡片区域 -->
      <div class="statistics-cards">
        <div class="stat-card">
          <div class="stat-card-icon total-icon">
            <i class="el-icon-money"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">总收费金额</div>
            <div class="stat-value total-value">
              {{ formatAmount(summary.chargeAmount) }}
              <span class="unit">元</span>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-icon refund-icon">
            <i class="el-icon-refund"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">退款金额</div>
            <div class="stat-value refund-value">
              {{ formatAmount(summary.refundAmount) }}
              <span class="unit">元</span>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-icon revenue-icon">
            <i class="el-icon-s-marketing"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">净营收金额</div>
            <div class="stat-value revenue-value">
              {{ formatAmount(summary.revenueAmount) }}
              <span class="unit">元</span>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-icon patient-icon">
            <i class="el-icon-user"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-label">就诊患者数</div>
            <div class="stat-value patient-value">
              {{ summary.revenuePatientCount || 0 }}
              <span class="unit">人</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 表格区域 -->
      <div class="table-container">
        <!-- 订单分类金额 -->
        <div class="table-section">
          <div class="section-title">订单分类金额</div>
          <el-table
              :data="categoryData"
              border
              stripe
              v-loading="loading"
              style="width: 100%"
          >
            <el-table-column prop="categoryName" label="分类名称" min-width="120" align="center" />
            <el-table-column prop="chargeAmount" label="收费金额" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.chargeAmount) }}
              </template>
            </el-table-column>
            <el-table-column prop="refundAmount" label="退款金额" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.refundAmount) }}
              </template>
            </el-table-column>
            <el-table-column prop="netAmount" label="净营收金额" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.netAmount) }}
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 支付方式金额 -->
        <div class="table-section" style="margin-top: 24px;">
          <div class="section-title">支付方式金额</div>
          <el-table
              :data="paymentData"
              border
              stripe
              v-loading="loading"
              style="width: 100%"
          >
            <el-table-column prop="payWayName" label="支付方式" min-width="120" align="center" />
            <el-table-column prop="chargeAmount" label="收费金额" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.chargeAmount) }}
              </template>
            </el-table-column>
            <el-table-column prop="refundAmount" label="退款金额" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.refundAmount) }}
              </template>
            </el-table-column>
            <el-table-column prop="netAmount" label="净营收金额" min-width="120" align="right">
              <template #default="{ row }">
                {{ formatAmount(row.netAmount) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { clinicRevenueOverview } from "@/api/reservation/outpatientCharge"; // 按实际路径调整

export default {
  name: "ClinicRevenueOverview",
  data() {
    return {
      loading: false,
      timeRange: [
        dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss')
      ],
      queryParams: {
        startTime: dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        endTime: dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss')
      },
      // 汇总数据
      summary: {
        chargeAmount: 0,
        refundAmount: 0,
        revenueAmount: 0,
        revenuePatientCount: 0
      },
      // 分类数据
      categoryData: [],
      // 支付方式数据
      paymentData: [],
      // 时间快捷选项
      pickerOptions: {
        shortcuts: [
          { text: '今天', onClick(picker) {
              const start = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }},
          { text: '昨天', onClick(picker) {
              const start = dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().subtract(1, 'day').endOf('day').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }},
          { text: '本周', onClick(picker) {
              const start = dayjs().startOf('week').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('week').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }},
          { text: '本月', onClick(picker) {
              const start = dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }},
          { text: '上月', onClick(picker) {
              const start = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }},
          { text: '本季度', onClick(picker) {
              const start = dayjs().startOf('quarter').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('quarter').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }},
          { text: '本年', onClick(picker) {
              const start = dayjs().startOf('year').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('year').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }}
        ]
      }
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const params = {
          startTime: this.queryParams.startTime,
          endTime: this.queryParams.endTime
        };
        const res = await clinicRevenueOverview(params);
        if (res.code === 200) {
          const data = res.data || {};
          this.summary = {
            chargeAmount: data.chargeAmount || 0,
            refundAmount: data.refundAmount || 0,
            revenueAmount: data.revenueAmount || 0,
            revenuePatientCount: data.revenuePatientCount || 0
          };
          this.categoryData = data.categoryAmounts || [];
          this.paymentData = data.paymentAmounts || [];
        } else {
          this.$message.error(res.message || '获取数据失败');
        }
      } catch (error) {
        console.error('加载诊所营收报表失败:', error);
        this.$message.error('系统异常，请联系管理员');
      } finally {
        this.loading = false;
      }
    },

    handleTimeChange(val) {
      if (val && val.length === 2) {
        this.queryParams.startTime = val[0];
        this.queryParams.endTime = val[1];
        this.loadData();
      }
    },

    handleReset() {
      this.timeRange = [
        dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss')
      ];
      this.queryParams.startTime = this.timeRange[0];
      this.queryParams.endTime = this.timeRange[1];
      this.loadData();
    },

    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0.00';
      const num = Number(amount);
      if (isNaN(num)) return '0.00';
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }
};
</script>

<style scoped lang="less">
.clinic-revenue-overview {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100%;

  .overview-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    .card-header {
      padding: 16px 24px;
      background: linear-gradient(135deg, #409EFF 0%, #36c6d9 100%);
      color: #fff;

      .header-title {
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 600;

        i {
          font-size: 24px;
          margin-right: 10px;
        }
      }
    }

    .query-form {
      padding: 16px 24px;
      border-bottom: 1px solid #ebeef5;
      background: #fafbfc;
    }

    .statistics-cards {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      padding: 24px;

      .stat-card {
        display: flex;
        align-items: center;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .stat-card-icon {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          margin-right: 16px;
          font-size: 28px;

          &.total-icon {
            background: linear-gradient(135deg, #e6f7ff, #bae7ff);
            color: #409EFF;
          }
          &.refund-icon {
            background: linear-gradient(135deg, #fff1f0, #ffccc7);
            color: #f56c6c;
          }
          &.revenue-icon {
            background: linear-gradient(135deg, #f6ffed, #d9f7be);
            color: #67C23A;
          }
          &.patient-icon {
            background: linear-gradient(135deg, #f9f0ff, #efdbff);
            color: #722ED1;
          }
        }

        .stat-card-content {
          flex: 1;

          .stat-label {
            font-size: 14px;
            color: #909399;
            margin-bottom: 8px;
          }

          .stat-value {
            font-size: 24px;
            font-weight: bold;

            .unit {
              font-size: 12px;
              font-weight: normal;
              margin-left: 4px;
              color: #909399;
            }

            &.total-value { color: #409EFF; }
            &.refund-value { color: #f56c6c; }
            &.revenue-value { color: #67C23A; }
            &.patient-value { color: #722ED1; }
          }
        }
      }
    }

    .table-container {
      padding: 0 24px 24px 24px;

      .table-section {
        .section-title {
          font-size: 16px;
          font-weight: 600;
          color: #303133;
          margin-bottom: 12px;
          padding-left: 12px;
          border-left: 4px solid #409EFF;
        }
      }
    }
  }
}

@media (max-width: 1200px) {
  .clinic-revenue-overview .overview-card .statistics-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .clinic-revenue-overview {
    padding: 12px;

    .overview-card {
      .card-header { padding: 12px 16px; }
      .query-form { padding: 12px 16px; }
      .statistics-cards {
        grid-template-columns: 1fr;
        gap: 12px;
        padding: 16px;
      }
      .table-container {
        padding: 0 16px 16px 16px;
        overflow-x: auto;
      }
    }
  }
}
</style>